import React, { Suspense } from 'react'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import PropTypes from 'prop-types'
import DocumentTitle from 'react-document-title'
import Sticky from 'react-stickynode'
import classNames from 'classnames'
import SearchBarContainer from 'tickets/containers/SearchBarContainer'
import { EmptySuspense, TabSuspense } from 'common/components/chips/StyledSuspense'
import { logError } from 'common/utils/errors'
import { cancelTicketSearchExpiryHandler } from 'tickets/redux/modules/user/searchTimeout'
import Loading from 'tickets/components/Loading'
import style from './TicketsApp.less'
import ErrorBoundaryContainer from 'common/containers/ErrorBoundaryContainer'
import ErrorView from 'common/components/misc/ErrorView'
import { retry } from 'common/utils/retry'

const TabsContainer = React.lazy(() => retry(() => import('tickets/containers/TabsContainer')))
const TicketMapContainer = React.lazy(() => retry(() => import('tickets/containers/TicketMapContainer')))
const TicketPanelContainer = React.lazy(() => retry(() => import('tickets/containers/TicketPanelContainer')))
const FerryCarSelectionContainer = React.lazy(() => retry(() => import('tickets/containers/FerryCarSelectionContainer')))
const WarningStripContainer = React.lazy(() => retry(() => import('tickets/containers/WarningStripContainer')))

class TicketsApp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
    }
  }

  componentDidMount() {
    this.props.performSearch()

    // Set incoming mode if available
    retry(() => import('qs').then(module => {
      const qs = module.default
      const modeId = qs.parse(this.props.location.search.slice(1)).mode
      this.props.onInitMode(modeId)

      const searchParams = qs.parse(this.props.location.search.slice(1))
      const errorTest = searchParams.errortest
      if (errorTest) {
        throw new Error('Error Test')
      }

    }).catch(error => {
      this.setState({
        error,
      })
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.error) {
      import('qs').then(module => {
        const qs = module.default
        const searchParams = qs.parse(this.props.location.search.slice(1))
        const prevSearchParams = qs.parse(prevProps.location.search.slice(1))

        const hasOriginChanged = this.props.originDestination.origin != prevProps.originDestination.origin
        const hasDestinationChanged = this.props.originDestination.destination != prevProps.originDestination.destination

        const hasODateTimeChanged = searchParams.oDateTime != prevSearchParams.oDateTime
        const hasIDateTimeChanged = searchParams.iDateTime != prevSearchParams.iDateTime
        const hasAdultsChanged = searchParams.adults != prevSearchParams.adults
        const hasSeniorsChanged = searchParams.seniors != prevSearchParams.seniors
        const hasYouthsChanged = searchParams.youths != prevSearchParams.youths
        const hasModeChanged = searchParams.mode != prevSearchParams.mode

        const hasChanged = hasOriginChanged || hasDestinationChanged || hasODateTimeChanged
          || hasIDateTimeChanged || hasAdultsChanged || hasSeniorsChanged || hasYouthsChanged

        if (hasChanged) {
          this.onRouteChanged()
        }

        if (hasModeChanged) {
          this.props.modeChanged(prevSearchParams.mode, searchParams.mode)
        }
      }).catch(error => {
        this.setState({
          error,
        })
      })
    } else {
      cancelTicketSearchExpiryHandler()
      logError(this.state.error)
    }
  }

  onRouteChanged() {
    import('lodash/debounce').then(module => {
      const debounce = module.default
      debounce(
        () => this.props.performSearch(),
        1000,
        { leading: true },
      )()
    })
  }

  componentDidCatch(error) {
    this.setState({
      error,
    })
  }

  renderWithError = () => {
    return <ErrorView error={this.state.error} />
  }

  render() {
    if (this.state.error) {
      return this.renderWithError()
    }
    const {
      originDestination: { origin, destination },
      hasHeader,
      error,
      hasOutbound,
      displayFerry,
      isInvalidHistoryState,
    } = this.props

    const rightColClasses = classNames({
      [style.rightCol]: true,
      [style.hasHeader]: (!hasOutbound && (!hasHeader || error)) && !isInvalidHistoryState,
    })

    return (
      <DocumentTitle title={`Tickets - ${origin} \u21E2 ${destination}`}>
        <div className={style.main}>
          <ErrorBoundaryContainer>
            <EmptySuspense>
              <SearchBarContainer />
            </EmptySuspense>
            <TabSuspense>
              <TabsContainer />
            </TabSuspense>
            <EmptySuspense>
              <WarningStripContainer />
            </EmptySuspense>
            {displayFerry
              && (
                <EmptySuspense>
                  <FerryCarSelectionContainer />
                </EmptySuspense>
              )
            }
            <div className={style.mainWrapper}>
              <div className={style.row}>
                <div className={style.leftCol}>
                  <Suspense fallback={<Loading />}>
                    <TicketPanelContainer />
                  </Suspense>
                </div>
                <div className={rightColClasses}>
                  <Sticky top={30}>
                  <EmptySuspense>
                    <TicketMapContainer />
                  </EmptySuspense>
                  </Sticky>
                </div>
              </div>
            </div>
          </ErrorBoundaryContainer>
        </div>
      </DocumentTitle>
    )
  }
}

TicketsApp.propTypes = {
  originDestination: PropTypes.shape({
    origin: PropTypes.string.isRequired,
    destination: PropTypes.string.isRequired,
  }).isRequired,
  hasHeader: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  hasOutbound: PropTypes.bool.isRequired,
  isPolling: PropTypes.bool,
  location: PropTypes.object.isRequired,
  performSearch: PropTypes.func.isRequired,
  displayFerry: PropTypes.bool,
  isInvalidHistoryState: PropTypes.bool,
  modeChanged: PropTypes.func.isRequired,
  onInitMode: PropTypes.func.isRequired,
}

export default TicketsApp
